import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { ApiError, apiFetch } from '../api/client.ts';
import { Button } from '../components/Button.tsx';
import { Card } from '../components/Card.tsx';
import { FormField } from '../components/FormField.tsx';
import { Select } from '../components/Select.tsx';
import { Textarea } from '../components/Textarea.tsx';

interface Category {
  id: string;
  name: string;
  slug: string;
  parentId: string | null;
  parentName: string | null;
}

interface Article {
  id: string;
  categoryId: string | null;
  slug: string;
  title: string;
  bodyHtml: string;
  audience: 'public' | 'internal';
  isPublished: boolean;
}

interface Props {
  mode: 'create' | 'edit';
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 80);
}

export function KbEditorPage({ mode }: Props): React.ReactElement {
  const navigate = useNavigate();
  const { slug: editSlug = '' } = useParams<{ slug?: string }>();
  const [categories, setCategories] = useState<Category[]>([]);
  const [article, setArticle] = useState<Article | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [categoryId, setCategoryId] = useState('');
  const [audience, setAudience] = useState<'public' | 'internal'>('internal');
  const [body, setBody] = useState('');
  const [isPublished, setIsPublished] = useState(false);

  // Inline category-create state. Shown when the user picks the "__new__"
  // sentinel option in the Category dropdown.
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryParentId, setNewCategoryParentId] = useState('');
  const [newCategoryBusy, setNewCategoryBusy] = useState(false);
  const [newCategoryError, setNewCategoryError] = useState<string | null>(null);

  async function createInlineCategory() {
    const name = newCategoryName.trim();
    if (!name) {
      setNewCategoryError('Name is required.');
      return;
    }
    setNewCategoryBusy(true);
    setNewCategoryError(null);
    try {
      const r = await apiFetch<{ category: Category }>('/api/kb/categories', {
        method: 'POST',
        json: {
          name,
          slug: slugify(name) || 'category',
          parentId: newCategoryParentId || null,
        },
      });
      // Refresh the list so the new category renders in its proper group,
      // then auto-select it.
      const list = await apiFetch<{ categories: Category[] }>('/api/kb/categories');
      setCategories(list.categories);
      setCategoryId(r.category.id);
      setShowNewCategory(false);
      setNewCategoryName('');
      setNewCategoryParentId('');
    } catch (err) {
      setNewCategoryError(err instanceof ApiError ? err.message : 'Failed to create category.');
    } finally {
      setNewCategoryBusy(false);
    }
  }

  useEffect(() => {
    apiFetch<{ categories: Category[] }>('/api/kb/categories')
      .then((r) => setCategories(r.categories))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    if (mode === 'create') return;
    apiFetch<{ article: Article }>(`/api/kb/articles/by-slug/${encodeURIComponent(editSlug)}`)
      .then((r) => {
        setArticle(r.article);
        setTitle(r.article.title);
        setSlug(r.article.slug);
        setSlugManuallyEdited(true);
        setCategoryId(r.article.categoryId ?? '');
        setAudience(r.article.audience);
        setBody(r.article.bodyHtml);
        setIsPublished(r.article.isPublished);
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Failed to load article.');
      });
  }, [mode, editSlug]);

  function handleTitleChange(v: string) {
    setTitle(v);
    if (!slugManuallyEdited && mode === 'create') setSlug(slugify(v));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const payload = {
        categoryId: categoryId || null,
        slug: slug || slugify(title),
        title,
        bodyHtml: body,
        audience,
        isPublished,
      };
      if (mode === 'create') {
        const res = await apiFetch<{ article: Article }>('/api/kb/articles', {
          method: 'POST',
          json: payload,
        });
        navigate(`/kb/${res.article.slug}`);
      } else {
        if (!article) return;
        const res = await apiFetch<{ article: Article }>(`/api/kb/articles/${article.id}`, {
          method: 'PATCH',
          json: payload,
        });
        navigate(`/kb/${res.article.slug}`);
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Save failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <Link to="/kb" className="text-sm text-[var(--color-muted)] hover:underline">
          ← Knowledgebase
        </Link>
        <h1 className="mt-1 text-2xl font-bold text-[var(--color-header)]">
          {mode === 'create' ? 'New article' : 'Edit article'}
        </h1>
      </div>

      {error && (
        <Card className="border-[var(--color-danger)]/30 bg-red-50/40">
          <p className="text-sm text-[var(--color-danger)]">{error}</p>
        </Card>
      )}

      <Card>
        <form onSubmit={submit} className="space-y-4">
          <FormField
            label="Title"
            name="title"
            required
            maxLength={300}
            value={title}
            onChange={(e) => handleTitleChange(e.target.value)}
          />
          <FormField
            label="Slug (URL)"
            name="slug"
            required
            maxLength={120}
            value={slug}
            onChange={(e) => {
              setSlug(slugify(e.target.value));
              setSlugManuallyEdited(true);
            }}
            hint="Auto-generated from title; you can override."
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Select
                label="Category"
                value={showNewCategory ? '__new__' : categoryId}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '__new__') {
                    setShowNewCategory(true);
                    setNewCategoryError(null);
                  } else {
                    setShowNewCategory(false);
                    setCategoryId(v);
                  }
                }}
              >
                <option value="">— uncategorized —</option>
                {categories
                  .filter((c) => c.parentId === null)
                  .map((parent) => (
                    <optgroup key={parent.id} label={parent.name}>
                      <option value={parent.id}>{parent.name} (top-level)</option>
                      {categories
                        .filter((c) => c.parentId === parent.id)
                        .map((sub) => (
                          <option key={sub.id} value={sub.id}>
                            &nbsp;&nbsp;{sub.name}
                          </option>
                        ))}
                    </optgroup>
                  ))}
                <option value="__new__">+ Create new category…</option>
              </Select>

              {showNewCategory && (
                <div className="mt-2 space-y-2 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-[var(--color-surface)]/50 p-3">
                  <FormField
                    label="New category name"
                    name="newCategoryName"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    autoFocus
                  />
                  <Select
                    label="Parent (optional)"
                    value={newCategoryParentId}
                    onChange={(e) => setNewCategoryParentId(e.target.value)}
                  >
                    <option value="">— top-level —</option>
                    {categories
                      .filter((c) => c.parentId === null)
                      .map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                  </Select>
                  {newCategoryError && (
                    <p className="text-xs text-[var(--color-danger)]">{newCategoryError}</p>
                  )}
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      onClick={() => void createInlineCategory()}
                      disabled={newCategoryBusy || !newCategoryName.trim()}
                    >
                      {newCategoryBusy ? 'Creating…' : 'Create + select'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowNewCategory(false);
                        setNewCategoryName('');
                        setNewCategoryParentId('');
                        setNewCategoryError(null);
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              <Link
                to="/admin/kb-categories"
                target="_blank"
                rel="noreferrer"
                className="mt-1 inline-block text-xs text-[var(--color-muted)] hover:underline"
              >
                Manage all categories →
              </Link>
            </div>
            <Select
              label="Audience"
              value={audience}
              onChange={(e) => setAudience(e.target.value as 'public' | 'internal')}
            >
              <option value="internal">Internal — agents only</option>
              <option value="public">Public — clients can read in portal</option>
            </Select>
          </div>
          <Textarea
            label="Body (HTML allowed; supports basic tags + links)"
            name="body"
            rows={16}
            required
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={isPublished}
              onChange={(e) => setIsPublished(e.target.checked)}
              className="h-4 w-4 accent-[var(--color-t3-blue)]"
            />
            Published (unchecked = draft, hidden from list)
          </label>
          <Button type="submit" disabled={busy || !title || !slug}>
            {busy ? 'Saving…' : mode === 'create' ? 'Create' : 'Save'}
          </Button>
        </form>
      </Card>
    </div>
  );
}
